import React from 'react';
import { Link } from 'react-router-dom';
import FighterCornerBars from '../FighterCornerBars/FighterCornerBars';
import type { VideoCardData } from '../../types/video-card.types';
import type { ContinueWatchingItem } from '../../utils/continue-watching';
import {
    formatVideoCardDate,
    formatVideoCardFightNumber,
    formatVideoCardParticipantName,
    orderVideoCardParticipants
} from '../../utils/video-card';
import { useWatchState } from '../../providers/watch-state.provider';
import { buildVideoCardWatchUrl } from '../../utils/watch-state';
import styles from './WatchMomentumPanel.module.scss';
import { useAnalyticsService } from '../../services/analytics.service';
import { ANALYTICS_EVENT_TYPES } from '../../types/analytics.types';

export type WatchMomentumVariant = 'section' | 'sidebar';

interface ContinueWatchingCardProps {
    item: ContinueWatchingItem;
}

interface ContinueWatchingRailProps {
    title?: string;
    subtitle?: string;
    items: ContinueWatchingItem[];
    isLoading?: boolean;
    variant?: WatchMomentumVariant;
}

interface SuggestedVideosRailProps {
    title?: string;
    subtitle?: string;
    videos: VideoCardData[];
    isLoading?: boolean;
    variant?: WatchMomentumVariant;
}

export interface WatchMomentumPanelProps {
    continueItems?: ContinueWatchingItem[];
    suggestedVideos?: VideoCardData[];
    isContinueLoading?: boolean;
    isSuggestedLoading?: boolean;
    variant?: WatchMomentumVariant;
    continueTitle?: string;
    continueSubtitle?: string;
    suggestedTitle?: string;
    suggestedSubtitle?: string;
    className?: string;
}

const getThumbnail = (item: ContinueWatchingItem) => item.card.thumbnailUrl
    ? `data:image/png;base64,${item.card.thumbnailUrl}`
    : null;

const getVideoThumbnail = (video: VideoCardData) => video.thumbnailUrl
    ? `data:image/png;base64,${video.thumbnailUrl}`
    : null;

const panelClassName = (variant: WatchMomentumVariant, className?: string) => [
    styles.panel,
    variant === 'sidebar' ? styles.sidebarPanel : styles.sectionPanel,
    className ?? ''
].filter(Boolean).join(' ');

const railClassName = (variant: WatchMomentumVariant) => [
    styles.rail,
    variant === 'sidebar' ? styles.sidebarRail : styles.sectionRail
].filter(Boolean).join(' ');

const ContinueWatchingCard = ({ item }: ContinueWatchingCardProps) => {
    const thumbnail = getThumbnail(item);
    const { trackEvent } = useAnalyticsService();

    return (
        <Link
            to={item.card.watchUrl}
            className={styles.continueCard}
            onClick={() => {
                void trackEvent({
                    eventType: ANALYTICS_EVENT_TYPES.CONTINUE_WATCHING_CLICKED,
                    videoId: item.card.id,
                    metadata: {
                        source: 'continue_watching_rail',
                        title: item.card.title,
                        progressPercent: item.progressPercent
                    }
                });
            }}
        >
            <span className={styles.continueThumb}>
                {thumbnail ? (
                    <img src={thumbnail} alt="" loading="lazy" />
                ) : (
                    <span className={styles.thumbnailFallback}>No thumbnail</span>
                )}
                <span className={styles.playBadge} aria-hidden="true">
                    <span className="material-symbols-outlined">play_arrow</span>
                </span>
            </span>
            <span className={styles.continueCopy}>
                <span className={styles.continueTitle}>{item.card.title}</span>
                <span className={styles.continueMeta}>
                    Resume at {item.resumeLabel} | Watched {item.lastWatchedLabel}
                </span>
                <span className={styles.progressTrack} aria-label={`${item.progressPercent}% watched`}>
                    <span
                        className={styles.progressFill}
                        style={{ width: `${item.progressPercent}%` }}
                    />
                </span>
            </span>
        </Link>
    );
};

const SuggestedVideoCard = ({ video }: { video: VideoCardData }) => {
    const thumbnail = getVideoThumbnail(video);
    const { getWatchState } = useWatchState();
    const { trackEvent } = useAnalyticsService();
    const watchState = getWatchState(video);
    const watchUrl = buildVideoCardWatchUrl(video, watchState);
    const orderedParticipants = orderVideoCardParticipants(video.participants ?? []);
    const leftParticipant = orderedParticipants[0];
    const rightParticipant = orderedParticipants[1];
    const leftName = formatVideoCardParticipantName(leftParticipant?.name);
    const rightName = formatVideoCardParticipantName(rightParticipant?.name);
    const eventDateLabel = formatVideoCardDate(video.eventDate);
    const fightNumberLabel = formatVideoCardFightNumber(video.fightNumber);
    const metaItems = [fightNumberLabel, eventDateLabel, video.eventName, video.venueName].filter(
        (value) => Boolean(value && value.trim())
    ) as string[];

    return (
        <Link
            to={watchUrl}
            className={styles.suggestedCard}
            onClick={() => {
                void trackEvent({
                    eventType: ANALYTICS_EVENT_TYPES.SUGGESTED_NEXT_CLICKED,
                    videoId: video.id,
                    metadata: {
                        source: 'suggested_video_rail',
                        title: video.title
                    }
                });
            }}
        >
            <span className={styles.suggestedThumb}>
                {thumbnail ? (
                    <img src={thumbnail} alt="" loading="lazy" />
                ) : (
                    <span className={styles.thumbnailFallback}>No thumbnail</span>
                )}
                <span className={styles.playBadge} aria-hidden="true">
                    <span className="material-symbols-outlined">play_arrow</span>
                </span>
                {watchState ? (
                    <>
                        <span className={styles.watchStateBadge}>
                            {watchState.kind === 'in-progress' ? 'Resume' : 'Watched'}
                        </span>
                        <span className={styles.thumbProgressTrack} aria-hidden="true">
                            <span
                                className={watchState.kind === 'in-progress' ? styles.thumbProgressFill : styles.thumbProgressFillComplete}
                                style={{ width: `${watchState.progressPercent}%` }}
                            />
                        </span>
                    </>
                ) : null}
            </span>
            <span className={styles.suggestedCopy}>
                <FighterCornerBars
                    leftName={leftName}
                    leftColor={leftParticipant?.cornerColor ?? null}
                    rightName={rightName}
                    rightColor={rightParticipant?.cornerColor ?? null}
                    layout="row"
                    size="sm"
                    stackOnMobile
                />
                <span className={styles.suggestedTitle}>{video.title}</span>
                {metaItems.length > 0 ? (
                    <span className={styles.suggestedMeta}>{metaItems.join(' | ')}</span>
                ) : null}
                {watchState ? (
                    <span className={styles.suggestedWatchMeta}>
                        {watchState.kind === 'in-progress'
                            ? `Resume at ${watchState.resumeLabel}`
                            : `Watched ${watchState.lastWatchedLabel}`}
                    </span>
                ) : null}
            </span>
        </Link>
    );
};

/**
 * Personal continue-watching rail for resumable viewing-history items.
 */
export const ContinueWatchingRail = ({
    title = 'Continue Watching',
    subtitle = 'Pick up where you left off.',
    items,
    isLoading = false,
    variant = 'section'
}: ContinueWatchingRailProps) => {
    if (!isLoading && items.length === 0) {
        return null;
    }

    return (
        <section className={styles.group} aria-label={title}>
            <div className={styles.header}>
                <h2>{title}</h2>
                {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <div className={railClassName(variant)}>
                {isLoading ? (
                    Array.from({ length: variant === 'sidebar' ? 3 : 4 }).map((_, index) => (
                        <div key={`continue-skeleton-${index}`} className={styles.continueSkeleton} aria-hidden="true">
                            <div className={styles.skeletonThumb} />
                            <div className={styles.skeletonCopy}>
                                <div className={styles.skeletonLine} />
                                <div className={styles.skeletonLineShort} />
                                <div className={styles.skeletonProgress} />
                            </div>
                        </div>
                    ))
                ) : items.map((item) => (
                    <ContinueWatchingCard key={item.id} item={item} />
                ))}
            </div>
        </section>
    );
};

/**
 * Contextual suggested-video rail for keeping users in a watch flow.
 */
export const SuggestedVideosRail = ({
    title = 'Suggested Videos',
    subtitle = 'More fights that match what you are watching.',
    videos,
    isLoading = false,
    variant = 'section'
}: SuggestedVideosRailProps) => {
    if (!isLoading && videos.length === 0) {
        return null;
    }

    return (
        <section className={styles.group} aria-label={title}>
            <div className={styles.header}>
                <h2>{title}</h2>
                {subtitle ? <p>{subtitle}</p> : null}
            </div>
            <div className={railClassName(variant)}>
                {isLoading ? (
                    Array.from({ length: variant === 'sidebar' ? 3 : 4 }).map((_, index) => (
                        <div key={`suggested-skeleton-${index}`} className={styles.continueSkeleton} aria-hidden="true">
                            <div className={styles.skeletonThumb} />
                            <div className={styles.skeletonCopy}>
                                <div className={styles.skeletonLine} />
                                <div className={styles.skeletonLineShort} />
                                <div className={styles.skeletonLine} />
                            </div>
                        </div>
                    ))
                ) : videos.map((video) => (
                    <SuggestedVideoCard key={video.id} video={video} />
                ))}
            </div>
        </section>
    );
};

/**
 * Combined watch momentum surface for continue-watching and suggested-video rails.
 */
const WatchMomentumPanel = ({
    continueItems = [],
    suggestedVideos = [],
    isContinueLoading = false,
    isSuggestedLoading = false,
    variant = 'section',
    continueTitle,
    continueSubtitle,
    suggestedTitle,
    suggestedSubtitle,
    className
}: WatchMomentumPanelProps) => {
    const hasContinue = isContinueLoading || continueItems.length > 0;
    const hasSuggestions = isSuggestedLoading || suggestedVideos.length > 0;

    if (!hasContinue && !hasSuggestions) {
        return null;
    }

    return (
        <div className={panelClassName(variant, className)}>
            <ContinueWatchingRail
                title={continueTitle}
                subtitle={continueSubtitle}
                items={continueItems}
                isLoading={isContinueLoading}
                variant={variant}
            />
            <SuggestedVideosRail
                title={suggestedTitle}
                subtitle={suggestedSubtitle}
                videos={suggestedVideos}
                isLoading={isSuggestedLoading}
                variant={variant}
            />
        </div>
    );
};

export default WatchMomentumPanel;
